
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Calendar, MapPin, Users, Trophy, ArrowRight } from "lucide-react";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [eventData, setEventData] = useState<any>(null);

  useEffect(() => {
    // Get event data from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const eventParam = urlParams.get('event');
    const registrationParam = urlParams.get('registration');

    if (eventParam && registrationParam) {
      try {
        const parsedEvent = JSON.parse(decodeURIComponent(eventParam));
        const parsedRegistration = JSON.parse(decodeURIComponent(registrationParam));
        setEventData({ ...parsedEvent, registration: parsedRegistration });

        // Add event to localStorage to simulate adding to "Mis eventos"
        const existingEvents = JSON.parse(localStorage.getItem('myUpcomingEvents') || '[]');
        const newEvent = {
          id: parsedEvent.id,
          title: parsedEvent.title,
          organizer: "Club Deportivo Central", // Default organizer
          sport: parsedEvent.sport,
          startDate: parsedEvent.date,
          endDate: parsedEvent.endDate || parsedEvent.date,
          time: parsedEvent.time,
          location: parsedEvent.location,
          category: parsedRegistration.selectedCategory || "General",
          partner: parsedRegistration.mode === 'parejas' ? parsedRegistration.partner?.name : undefined,
          status: "upcoming" as const,
          inscriptionStatus: "Confirmada",
          participants: parsedEvent.participants,
          maxParticipants: parsedEvent.maxParticipants,
          registrationFee: parsedEvent.registrationFee
        };

        // Check if event is not already added
        const isAlreadyAdded = existingEvents.some((event: any) => event.id === parsedEvent.id);
        if (!isAlreadyAdded) {
          existingEvents.push(newEvent);
          localStorage.setItem('myUpcomingEvents', JSON.stringify(existingEvents));
        }
      } catch (error) {
        console.error('Error parsing event data:', error);
        navigate('/');
      }
    } else {
      // If no event data, redirect to home
      navigate('/');
    }
  }, [navigate]);

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSportColor = (sport: string) => {
    switch (sport?.toLowerCase()) {
      case 'pádel':
        return 'bg-emerald-100 text-emerald-800';
      case 'tenis':
        return 'bg-green-100 text-green-800';
      case 'pickleball':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!eventData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Procesando confirmación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* Success Header */}
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">¡Pago Exitoso!</h1>
          <p className="text-lg text-white/80">Tu inscripción ha sido confirmada</p>
        </div>

        {/* Event Confirmation Card */}
        <Card className="bg-white/80 backdrop-blur-sm border-green-200">
          <CardHeader>
            <CardTitle className="text-xl text-green-800 flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Inscripción Confirmada
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{eventData.title}</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className={getSportColor(eventData.sport)}>
                  {eventData.sport}
                </Badge>
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {eventData.registration?.mode === 'individual' ? 'Individual' : 'Parejas'}
                </Badge>
                {eventData.registration?.partner && (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                    Con {eventData.registration.partner.name}
                  </Badge>
                )}
              </div>
            </div>

            <div className="grid gap-3">
              <div className="flex items-center text-gray-700">
                <Calendar className="h-4 w-4 mr-3 text-[#3D6B4F]" />
                <span>{formatEventDate(eventData.date)}</span>
              </div>
              
              <div className="flex items-center text-gray-700">
                <MapPin className="h-4 w-4 mr-3 text-red-500" />
                <span>{eventData.location}</span>
              </div>
              
              <div className="flex items-center text-gray-700">
                <Users className="h-4 w-4 mr-3 text-purple-500" />
                <span>Categoría: {eventData.registration?.selectedCategory || 'General'}</span>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-700">Total pagado:</span>
                <span className="text-2xl font-bold text-green-600">€{eventData.registration?.amount || eventData.registrationFee}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps Card */}
        <Card className="bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg text-gray-900">¿Qué sigue?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#3D6B4F] text-sm font-bold">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Revisa tu email</p>
                  <p className="text-sm text-gray-600">Te hemos enviado un email de confirmación con todos los detalles del evento.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#3D6B4F] text-sm font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Prepárate para el evento</p>
                  <p className="text-sm text-gray-600">El evento aparecerá en tu sección "Mis Eventos" para que puedas hacer seguimiento.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#3D6B4F] text-sm font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">¡Disfruta del torneo!</p>
                  <p className="text-sm text-gray-600">Llega 15 minutos antes del horario programado.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button 
            onClick={() => {
              console.log('Navigating to home with current user:', user?.id);
              navigate('/', { replace: true });
            }}
            className="flex-1 bg-[#3D6B4F] hover:bg-[#345D44] h-12"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            Ir a Mis Eventos
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => {
              console.log('Navigating to home with current user:', user?.id);
              navigate('/', { replace: true });
            }}
            className="flex-1 h-12"
          >
            Volver al Inicio
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
